import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { Download } from '@meitu/saas-editor-core/es/editor/extensions/download';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import type { EventEmitter } from '@meitu/saas-editor-core/es/framework';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import ImageEffectAction from '@meitu/saas-editor-core/es/layers/image-layer/utils/image-effect-action';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { BatchTemplateLabelType, IBatchTemplateData, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import { DownloadImgType } from '@/constants';
import { useLocaleStore } from '@/i18n';
import { getBase64ByObjectUrl } from '@/utils/file-kit';
import { objectURLHelper } from '@/utils/object-url-kit';

const getLayer = (layerId: string, layers: ILayerProps[]) => {
    let selectLayer: ILayerProps | null = null;
    traverseTrees(layers, (layer: ILayerProps) => {
        if (layer.id === layerId) {
            selectLayer = layer;
        }
    });

    return selectLayer;
};

type BatchTemplateProps = {
    editor: Editor;
    events: EventEmitter;
    // 从 useBatchTemplateDataState 搬迁
    batchTemplateData: IBatchTemplateData | null;
    // 从 useSetBatchTemplateDataAction 搬迁
    setBatchTemplateData: (data: any) => void;
    // 从旧代码 import { filterEmptyData } from '../moreCollapse/plugins/batchTemplate'; 搬迁
    filterEmptyData: (batchTemplateData: IBatchTemplateData) => IBatchTemplateData;
};
export const BatchTemplate = React.memo(
    ({ editor, events, batchTemplateData, setBatchTemplateData, filterEmptyData }: BatchTemplateProps) => {
        const [isBatchTemplateGenerate, setIsBatchTemplateGenerate] = useState(false);

        const activeIndex = editor.active.getActiveStageIndex();
        const extraInfo = editor.stage.getExtraInfo(activeIndex);

        const { $t } = useLocaleStore();

        function setTemplateData() {
            if (!batchTemplateData || isBatchTemplateGenerate) {
                return;
            }

            batchTemplateData.headerData = batchTemplateData.headerData.map((item: any) => {
                item.layerId = '';
                item.info = undefined;
                return item;
            });
            setBatchTemplateData({
                headerData: [...batchTemplateData.headerData],
                contentData: [...batchTemplateData.contentData],
            });
        }
        // 切换画布的时候要置空
        useEffect(() => {
            const handler = editor.active.onStageChange(() => {
                setTemplateData();
            });
            return () => editor.off(handler);
        }, []);

        useEffect(() => {
            // 舞台快捷操作按钮点击事件
            const generateBatchTemplate = async () => {
                try {
                    if (!batchTemplateData) {
                        return;
                    }
                    setIsBatchTemplateGenerate(true);

                    const layer = editor.active.getPick();
                    if (layer) {
                        ImageEffectAction.closeGLRender(layer);
                        editor.getInstance(ImageLayer).updateLayer(layer);
                    }

                    editor.active.delete();
                    const filterBatchTemplateData = filterEmptyData(batchTemplateData);
                    editor.tip.showMask({
                        loading: true,
                        text: $t((v) => v.collapse.moreCollapse.batchTool.batchTemplate.isGeneratingText),
                        background: true,
                    });
                    const { headerData, contentData } = filterBatchTemplateData;
                    const layers = editor.active.getLayers();

                    for (let i = 0; i < contentData.length; i++) {
                        const dataItem = contentData[i];

                        const stage = {
                            width: layers[0].width,
                            height: layers[0].height,
                            layers: cloneDeep(layers),
                        };

                        for (let subIndex = 0; subIndex < dataItem.length; subIndex++) {
                            const { layerId, labelType, fillType } = headerData[subIndex];
                            const layer = getLayer(layerId, stage.layers);

                            if (labelType === BatchTemplateLabelType.image && dataItem[subIndex]) {
                                const file = await getBase64ByObjectUrl(dataItem[subIndex]);
                                URL.revokeObjectURL(dataItem[subIndex]);
                                if (layer && file.type === 'image/svg+xml') {
                                    const newLayer = await editor
                                        .getInstance(ImageLayer)
                                        .replaceImageLayerByVectorFile(layer, file, fillType);
                                    Object.assign(layer, newLayer);
                                } else if (layer) {
                                    await editor
                                        .getInstance(ImageLayer)
                                        .replaceImageLayerByImageFile(layer, file, fillType);
                                }
                            } else if (labelType === BatchTemplateLabelType.text && `${dataItem[subIndex]}`) {
                                if (layer) {
                                    const newLayer = await editor
                                        .getInstance(TextLayerExtension)
                                        .replaceLayerText(layer, `${dataItem[subIndex]}`, {
                                            resetLayerID: true,
                                            keepConstantWidth: true,
                                        });
                                    Object.assign(layer, newLayer);
                                }
                            }
                        }
                        const canvas = await editor
                            .getInstance(Download)
                            .drawLayers(cloneDeep(stage.layers), DownloadImgType.png);

                        const url = await objectURLHelper.create(canvas);

                        editor.stage.insertToNext(
                            stage.layers,
                            cloneDeep({
                                ...extraInfo,
                                stagePreviewInfo: {
                                    url,
                                },
                            }),
                            false,
                            '30_119',
                        );
                    }
                    // 保存上一下操作的数据
                    // setBatchTemplateData(null);
                    editor.tip.hideMask();
                    window._stat_track('aisc_create_success', {
                        func: 'batch_template',
                        entrance: 'more',
                        size_num: filterBatchTemplateData?.contentData.length || 0,
                    });
                    setIsBatchTemplateGenerate(false);
                    setTemplateData();
                } catch (error) {
                    console.error('批量套版错误===', error);
                    setIsBatchTemplateGenerate(false);
                    setTemplateData();
                }
            };
            const handler = events.on('generateBatchTemplate', generateBatchTemplate);
            return () => {
                events.off(handler);
            };
        }, [batchTemplateData]);

        return <></>;
    },
);
